import { Dispatch } from "react";
import { AppState } from "./configureStore";
import { createSlice, PayloadAction, AnyAction } from '@reduxjs/toolkit';
import { ILoadableState } from "../definitions/ILoadableState";
import { client } from "../App";
import { CMDetail, CMSummary, CMSummaryResponse, NameBySSNUMCResponse, NameBySSNUMC, CMDetailResponse, CMAddEdit, CMDeleteRow, CMAddEditResponse } from "../DCR.dtos"

export interface EncryptParamResponse {
    encryptedParamString: string;
}
// eslint-disable-next-line
export interface IDetailsState extends ILoadableState {
    cmSummaryInfo: CMSummaryResponse;
    cmDetailsInfo: CMDetailResponse;
    cmAddEditInfo: CMAddEditResponse;
    cmDeleteRowInfo: CMAddEditResponse;
    nameBySSNUMCInfo: NameBySSNUMCResponse;
    errorMessage?: string;
    isNameLoaded?: boolean;
    isNameLoding?: boolean;
    isDetailsLoaded?: boolean;
    isSummaryLoaded?: boolean;
    isSummaryLoading?: boolean;
    isAddEditSuccess?: boolean;
    isRowDeleted?: boolean;
    rowDeleteErrorMessage?: string;
    isRowDeleteError?: boolean;
    isAddEditError?: boolean;
    addEditErrorMessage?: string;
    cmHistoryRecordOrgType?: string;
    cmHistoryRecordStatus?: string;
    cmHistoryRecordUserName?: string;
    cmHistoryFilePayrollDate?: string;
    cmHistoryStatusDate?: string;
    cmHistoryScheduleDate?: string;
    isPrintParamFetching?: boolean;
    isPrintParamFetched?: boolean;
    printPDFEncryptedParams?: string;
    cmHistoryRecordSchedType?: string;
    isChangeLogParamFetching?: boolean;
    isChangeLogParamFetched?: boolean;
    changeLogReportEncryptedParams?: string;
}

const initialDetailsState: IDetailsState = {
    cmDetailsInfo: {} as CMDetailResponse,
    cmSummaryInfo: {} as CMSummaryResponse,
    cmAddEditInfo: {} as CMAddEditResponse,
    cmDeleteRowInfo: {} as CMAddEditResponse,
    nameBySSNUMCInfo: {} as NameBySSNUMCResponse,
    isLoading: false,
    isLoaded: false,
    isNameLoaded: false,
    isDetailsLoaded: false,
    isSummaryLoaded: false,
    isSummaryLoading: false,
    isAddEditSuccess: false,
    isRowDeleted: false,
    isRowDeleteError: false,
    isAddEditError: false,
    cmHistoryRecordOrgType: '',
    cmHistoryRecordStatus: '',
    cmHistoryRecordUserName: '',
    addEditErrorMessage: '',
    cmHistoryFilePayrollDate: '',
    cmHistoryStatusDate: '',
    cmHistoryScheduleDate: '',
    isPrintParamFetching: false,
    isPrintParamFetched: false,
    printPDFEncryptedParams: '',
    cmHistoryRecordSchedType: '',
    isChangeLogParamFetching: false,
    isChangeLogParamFetched: false,
    changeLogReportEncryptedParams: ''
}

const slice = createSlice({
    name: "details",
    initialState: initialDetailsState,
    reducers: {
        resetState: () => {
            return {
                ...initialDetailsState
            }
        },
        requestCMDetails: (state: IDetailsState) => {
            return {
                ...state,
                isLoading: true,
                isLoaded: false
            }
        },
        receiveCMDetails: (state: IDetailsState, action: PayloadAction<{
            cmDetailsInfoResponse: CMDetailResponse;
        }>) => {
            const cmDetailsInfo = action.payload.cmDetailsInfoResponse;
            return {
                ...state,
                cmDetailsInfo,
                isDetailsLoaded: true,
                isLoading: false,
                isLoaded: (state.isDetailsLoaded ?? false) && (state.isSummaryLoaded ?? false),
                error: false
            }
        },
        receiveCMDetailsError: (state: IDetailsState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isLoading: false,
                isLoaded: true,
                error: true,
                errorMessage: message
            }
        },
        requestCMSummary: (state: IDetailsState) => {
            return {
                ...state,
                isLoading: true,
                isSummaryLoading: true,
                isLoaded: false
            }
        },
        receiveCMSummary: (state: IDetailsState, action: PayloadAction<{
            cmSummaryInfoResponse: CMSummaryResponse;
        }>) => {
            const cmSummaryInfo = action.payload.cmSummaryInfoResponse;
            return {
                ...state,
                cmSummaryInfo,
                isLoading: false,
                isSummaryLoading: false,
                isSummaryLoaded: true,
                isLoaded: (state.isDetailsLoaded ?? false) && (state.isSummaryLoaded ?? false),
                error: false
            }
        },
        receiveCMSummaryError: (state: IDetailsState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isLoading: false,
                isSummaryLoading: false,
                isLoaded: true,
                error: true,
                errorMessage: message
            }
        },
        requestName: (state: IDetailsState) => {
            return {
                ...state,
                isNameLoding: true,
                isNameLoaded: false
            }
        },
        receiveName: (state: IDetailsState, action: PayloadAction<{
            nameBySSNUMCInfoResponse: NameBySSNUMCResponse;
        }>) => {
            const nameBySSNUMCInfo = action.payload.nameBySSNUMCInfoResponse;
            return {
                ...state,
                nameBySSNUMCInfo,
                isNameLoding: false,
                isNameLoaded: true,
                error: false
            }
        },
        receiveNameError: (state: IDetailsState, action: PayloadAction<{ message: string }>) => {   
            const { message } = action.payload;
            return {
                ...state,
                isNameLoding: false,
                isNameLoaded: true,
                nameBySSNUMCInfo: {} as NameBySSNUMCResponse,
                errorMessage: message
            }
        },
        requestAddEdit: (state: IDetailsState) => {
            return {
                ...state,
                isAddEditSuccess: false,
                isAddEditError: false
            }
        },
        receiveAddEdit: (state: IDetailsState, action: PayloadAction<{
            addEditInfoResponse: CMAddEditResponse;
        }>) => {
            const cmAddEditInfo = action.payload.addEditInfoResponse;
            return {
                ...state,
                cmAddEditInfo,
                isAddEditSuccess: true,
                isAddEditError: false
            }
        },
        receiveAddEditError: (state: IDetailsState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isAddEditSuccess: false,
                isAddEditError: true,
                addEditErrorMessage: message
            }
        },
        requestDeleteRow: (state: IDetailsState) => {
            return {
                ...state,
                isRowDeleted: false,
                isRowDeleteError: false,
            }
        },
        receiveDeleteRow: (state: IDetailsState, action: PayloadAction<{
            deleteRowInfoResponse: CMAddEditResponse;
        }>) => {
            const cmDeleteRowInfo = action.payload.deleteRowInfoResponse;
            return {
                ...state,
                cmDeleteRowInfo,
                isRowDeleted: true,
                isRowDeleteError: false
            }
        },
        receiveDeleteRowError: (state: IDetailsState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isRowDeleted: false,
                isRowDeleteError: true,
                rowDeleteErrorMessage: message
            }
        },
        receiveDefaultCMSummary: (state: IDetailsState, action: PayloadAction<{ orgType: string; status: string; userName: string; filePayrollDate: string; statusDate: string; scheduleDate: string; schedType: string }>) => {
            const { orgType, status, userName, filePayrollDate, statusDate, scheduleDate, schedType } = action.payload;
            return {
                ...state,
                cmHistoryRecordOrgType: orgType,
                cmHistoryRecordStatus: status,
                cmHistoryRecordUserName: userName,
                cmHistoryFilePayrollDate: filePayrollDate,
                cmHistoryStatusDate: statusDate,
                cmHistoryScheduleDate: scheduleDate,
                isPrintParamFetching: false,
                isPrintParamFetched : false,
                printPDFEncryptedParams: "",
                cmHistoryRecordSchedType: schedType
            }
        },
        requestEncryptPrintParam: (state: IDetailsState) => {
            return {
                ...state,
                isPrintParamFetching: true,
                isPrintParamFetched: false
            }
        },
        receiveEncryptPrintParam: (state: IDetailsState, action: PayloadAction<{ encryptedStringResponse: string }>) => {
            const { encryptedStringResponse } = action.payload
            return {
                ...state,
                printPDFEncryptedParams: encryptedStringResponse,
                isPrintParamFetching: false,
                isPrintParamFetched: true
            }
        },
        requestResetPrintState: (state: IDetailsState) => {
            return {
                ...state,
                isPrintParamFetching: false,
                isPrintParamFetched: false,
                printPDFEncryptedParams: ""
            }
        },
        requestChangeLogEncryptParam: (state: IDetailsState) => {
            return {
                ...state,
                isChangeLogParamFetching: true,
                isChangeLogParamFetched: false
            }
        },
        receiveChangeLogEncryptParam: (state: IDetailsState, action: PayloadAction<{ encryptedStringResponse: string }>) => {
            const { encryptedStringResponse } = action.payload
            return {
                ...state,
                changeLogReportEncryptedParams: encryptedStringResponse,
                isChangeLogParamFetching: false,
                isChangeLogParamFetched: true
            }
        },
        requestResetChangeLogReportState: (state: IDetailsState) => {
            return {
                ...state,
                isChangeLogParamFetching: false,
                isChangeLogParamFetched: false,
                changeLogReportEncryptedParams: ""
            }
        },
    }
})

const {
    resetState,
    requestCMDetails,
    receiveCMDetails,
    receiveCMDetailsError,
    requestCMSummary,
    receiveCMSummary,
    receiveCMSummaryError,
    requestName,
    receiveName,
    receiveNameError,
    receiveDefaultCMSummary,
    requestAddEdit,
    receiveAddEdit,
    receiveAddEditError,
    requestDeleteRow,
    receiveDeleteRow,
    receiveDeleteRowError,
    requestEncryptPrintParam,
    receiveEncryptPrintParam,
    requestResetPrintState,
    requestChangeLogEncryptParam,
    receiveChangeLogEncryptParam,
    requestResetChangeLogReportState

} = slice.actions;

function setCmDetailsDefaults(orgType: string, status: string, userName: string, filePayrollDate: string, statusDate: string, scheduleDate: string, schedType: string) {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(receiveDefaultCMSummary({ orgType, status, userName, filePayrollDate, statusDate, scheduleDate, schedType }));
    }
}

function fetchCMDetails(ccaId: string, page: number, rowsPerPage: number, all: string, isSummaryCall: boolean, isDetailsGridCall: boolean, checkLoading: boolean) {

    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {

        const cmDetailsState = getState().cmDetails;
        if (checkLoading && (cmDetailsState.isLoading || cmDetailsState.isLoaded)) {
            // return;
        }

        if (isSummaryCall) {
            dispatch(requestCMSummary());
            try {
                const cmSummaryInfoResponse = await client.post(new CMSummary({ ccaId: ccaId }));

                //Check for errors
                if (cmSummaryInfoResponse.responseStatus)
                    throw cmSummaryInfoResponse.responseStatus.message;

                dispatch(receiveCMSummary({ cmSummaryInfoResponse }));

            }
            catch (e) {
                console.log(e);
                dispatch(receiveCMSummaryError({ message: e }))
            }
        }
        if (isDetailsGridCall) {
            dispatch(requestCMDetails());
            try {
                const cmDetailsInfoResponse = await client.post(new CMDetail({ ccaId: ccaId, page: page, rowsPerPage: rowsPerPage, all: all }));

                //Check for errors
                if (cmDetailsInfoResponse.responseStatus)
                    throw cmDetailsInfoResponse.responseStatus.message;

                dispatch(receiveCMDetails({ cmDetailsInfoResponse }));
            }
            catch (e) {
                console.log(e);
                dispatch(receiveCMDetailsError({ message: e }))
            }
        }
    }
}

function setEncyptedPrintParams(ccaId: string, filePayrollDate: string, status: string, statusDate: string, totalRecords: string, domain: string, userName: string, organization: string, umcID: string) {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(requestEncryptPrintParam());
        try {

            const url = `dcrapi/Encrypt/EncryptPrintPram`;

            const data = {
                "ccaId": ccaId,
                "filePayrollDate": filePayrollDate,
                "status": status,
                "statusDate": statusDate,
                "totalRecords": totalRecords,
                "domain": domain,
                "userName": userName,
                "organization": organization,
                "umcID": umcID,
                "apiUrl": client.baseUrl
            };

            const requestOptions = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                },
                dataType: "json",
                body: JSON.stringify(data)
            }
            const response = await fetch(url, requestOptions);
            const encryptPrintPramResponse: EncryptParamResponse = await response.json();
            dispatch(receiveEncryptPrintParam({ encryptedStringResponse: encryptPrintPramResponse.encryptedParamString }))
        }
        catch (ex) {
            console.log(ex);
        }
    }
}

function fetchNameBySSNUMC(umcId: string, ssn: string) {

    return async (dispatch: Dispatch<AnyAction>) => {

        dispatch(requestName());

        try {
            const nameBySSNUMCInfoResponse = await client.post(new NameBySSNUMC({ umcId: umcId, ssn: ssn }));
            //Check for errors
            if (nameBySSNUMCInfoResponse.responseStatus)
                throw nameBySSNUMCInfoResponse.responseStatus.message;

            dispatch(receiveName({ nameBySSNUMCInfoResponse }));
        }
        catch (e) {
            console.log(e);
            dispatch(receiveNameError({ message: e }))
        }
    }
}

function fetchAddEdit(tran: string, fileType: string) {

    return async (dispatch: Dispatch<AnyAction>) => {

        dispatch(requestAddEdit());

        try {
            const addEditInfoResponse = await client.post(new CMAddEdit({ tran: tran, fileType: fileType }));

            //Check for errors
            if (addEditInfoResponse.responseStatus)
                throw addEditInfoResponse.responseStatus.message;

            dispatch(receiveAddEdit({ addEditInfoResponse }));
        }
        catch (e) {
            console.log(e);
            dispatch(receiveAddEditError({ message: e }))
        }
    }
}

function fetchDeleteRow(tran: string) {

    return async (dispatch: Dispatch<AnyAction>) => {

        dispatch(requestDeleteRow());

        try {
            const deleteRowInfoResponse = await client.post(new CMDeleteRow({ tran: tran }));

            //Check for errors
            if (deleteRowInfoResponse.responseStatus)
                throw deleteRowInfoResponse.responseStatus.message;

            dispatch(receiveDeleteRow({ deleteRowInfoResponse }));
        }
        catch (e) {
            console.log(e);
            dispatch(receiveDeleteRowError({ message: e }))
        }
    }
}

function resetPrintState() {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(requestResetPrintState());
    }
}

function fetchEncryptedChangeLogParams(ccaId: string, starGenApiURL: string) {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(requestChangeLogEncryptParam());
        try {

            const url = `dcrapi/Encrypt/EncryptChangeLogReportParam`;

            const data = {
                "ccaId": ccaId,
                "dcrApiUrl": client.baseUrl,
                "starGenApiURL": starGenApiURL
            };

            const requestOptions = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                },
                dataType: "json",
                body: JSON.stringify(data)
            }
            const response = await fetch(url, requestOptions);
            const encryptParamResponse: EncryptParamResponse = await response.json();
            dispatch(receiveChangeLogEncryptParam({ encryptedStringResponse: encryptParamResponse.encryptedParamString }))
        }
        catch (ex) {
            console.log(ex);
        }
    }
}

function resetChangeLogreportState() {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(requestResetChangeLogReportState());
    }
}

export const detailsReducer = slice.reducer;

export const detailsActionCreators = {
    fetchCMDetails,
    fetchNameBySSNUMC,
    setCmDetailsDefaults,
    fetchAddEdit,
    fetchDeleteRow,
    resetState,
    setEncyptedPrintParams,
    resetPrintState,
    fetchEncryptedChangeLogParams,
    resetChangeLogreportState
};